// A staged template's script stage below its tiles (live's shared `input-chips` and
// `script-section`): the optional text chips, and the script with its lines (Podcast Ad's,
// Street Interview Ad's).
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Popover,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { PencilLine, Plus, WandSparkles, X } from "lucide-react";
import { Fragment, useState, type ReactNode } from "react";

import { GENERATE_SCRIPT as GENERATE_SCRIPT_LABEL } from "../../../platform/scripts/ScriptActions";
import { FieldLabel } from "../../../ui/FieldLabel";
import { countWords } from "./podcast-script";
import classes from "./podcast.module.css";

/** Live's cap on a line as typed (`MAX_SCRIPT_LINE_LENGTH`). */
const MAX_LINE_LENGTH = 500;
/** Live's popover width (`InputChip`'s `DROPDOWN_WIDTH`). */
const CHIP_WIDTH = 357;

export interface InputChipProps {
  label: string;
  value: string;
  placeholder: string;
  /** Hover hint of the empty chip; a filled chip shows its value instead. */
  tooltip: string;
  maxLength: number;
  disabled: boolean;
  onChange: (value: string) => void;
}

/**
 * Live's optional text chip (`InputChip`, capture 125): empty, a dashed "+ Topic" whose popover
 * has the input, Save (off while empty) and Cancel, Enter saving; filled, a primary pill whose
 * tooltip is its value and whose x clears it.
 */
export function InputChip(props: InputChipProps) {
  const { label, value, placeholder, tooltip, maxLength, disabled, onChange } = props;
  const [opened, setOpened] = useState(false);
  const filled = value !== "";
  return (
    <Popover
      trapFocus
      position="top-start"
      width={CHIP_WIDTH}
      opened={opened}
      disabled={disabled}
      onChange={setOpened}
    >
      <Popover.Target>
        <span className={classes.chip} data-filled={filled || undefined} data-disabled={disabled || undefined}>
          <Tooltip label={filled ? value : tooltip} multiline maw={280} withArrow disabled={opened}>
            <UnstyledButton
              className={classes.chipTrigger}
              disabled={disabled}
              aria-label={filled ? `${label}: ${value}` : label}
              onClick={() => setOpened((open) => !open)}
            >
              <Plus size={14} className={classes.chipIcon} aria-hidden />
              <span>{label}</span>
            </UnstyledButton>
          </Tooltip>
          {filled ? (
            <UnstyledButton
              className={classes.chipClear}
              aria-label={`Clear ${label}`}
              disabled={disabled}
              onClick={() => onChange("")}
            >
              <X size={14} aria-hidden />
            </UnstyledButton>
          ) : null}
        </span>
      </Popover.Target>
      <Popover.Dropdown className={classes.chipDropdown}>
        {/* Mounted while open: each opening starts from the chip's value. */}
        <ChipForm
          label={label}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          onSave={(next) => {
            onChange(next);
            setOpened(false);
          }}
          onCancel={() => setOpened(false)}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

function ChipForm(props: {
  label: string;
  value: string;
  placeholder: string;
  maxLength: number;
  onSave: (value: string) => void;
  onCancel: () => void;
}) {
  const { label, placeholder, maxLength, onSave, onCancel } = props;
  const [typed, setTyped] = useState(props.value);
  const trimmed = typed.trim();
  const save = () => {
    if (trimmed !== "") onSave(trimmed);
  };
  return (
    <Stack gap={12}>
      <TextInput
        data-autofocus
        label={label}
        value={typed}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(event) => setTyped(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          save();
        }}
      />
      <Group gap={8}>
        <Button disabled={trimmed === ""} onClick={save}>
          Save
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </Group>
    </Stack>
  );
}

/** A script's line as the section shows it: its number, speaker and beat, and the words. A
 * script of one speaker (Pixar Talking Ad's) names none: live shows no badge. */
export interface ScriptRow {
  id: number;
  speaker: string | null;
  beat: string;
  text: string;
}

export interface ScriptSectionProps {
  /** The lines, or none before the script is generated or written. */
  lines: readonly ScriptRow[] | null;
  /** Live's meta line (`toScriptMeta`), with the lines. */
  meta: string | null;
  /** Live's info tooltip on "Script". */
  tooltip: string;
  /** The speaker live marks (`primarySpeaker`): the host, the interviewer; none with one. */
  primary: string | null;
  /** The most words a line has (`maxCountAt`); a count over it turns red, no error. */
  maxWords: (index: number) => number;
  /** Live's empty hint after the wand, and after the pencil where there is one. */
  hint: { wand: string; pencil?: string };
  /** The pencil's label ("Write the lines yourself"); none, and only the wand writes (Claymation
   * Ad's). */
  writeLabel?: string;
  /** A director writes: live's loader over the lines. */
  writing: boolean;
  /** Nothing changes: a director or a still works, or the script is approved. */
  locked: boolean;
  /** What keeps the script from approval, once its lines were edited (live's error line). */
  issue: string | null;
  /** Live's warning under it after the director's redo. */
  warning: string | null;
  onGenerate: () => void;
  onWrite?: () => void;
  onLine: (index: number, text: string) => void;
  /** A line's words as counted against its most (`countUnits`); plain words by default. */
  countUnits?: (text: string) => number;
  /** Above the lines, beside each line and after them (live's `ScriptLinesEditor` slots:
   * Claymation Ad's summary, shot headers, "Says:" rows and end card). */
  head?: ReactNode;
  lineHead?: (index: number) => ReactNode;
  lineFoot?: (index: number) => ReactNode;
  foot?: ReactNode;
}

/**
 * Live's script section (`script-section` and `ScriptLinesEditor`; captures 115, 126 and 145):
 * "Script" with its info tooltip; at the right the meta line, the wand (Generate Script) and the
 * pencil. Without a script, live's hint; with one, a row a line: its number, the speaker (the
 * primary one marked), the beat, the words to edit and their count against the line's most.
 */
export function ScriptSection(props: ScriptSectionProps) {
  const { lines, meta, tooltip, primary, maxWords, hint, writeLabel } = props;
  const { writing, locked, issue, warning, onGenerate, onWrite, onLine } = props;
  const { countUnits = countWords, head, lineHead, lineFoot, foot } = props;
  return (
    <section aria-label="Script">
      <div className={classes.scriptHeader}>
        <Text component="div" className={classes.scriptTitle}>
          <FieldLabel label="Script" info={tooltip} spoken />
        </Text>
        <Group gap={8} wrap="nowrap">
          {meta === null ? null : <Text className={classes.meta}>{meta}</Text>}
          <Tooltip label={GENERATE_SCRIPT_LABEL} withArrow>
            <ActionIcon
              variant="subtle"
              size={36}
              aria-label={GENERATE_SCRIPT_LABEL}
              disabled={locked}
              onClick={onGenerate}
            >
              <WandSparkles size={16} aria-hidden />
            </ActionIcon>
          </Tooltip>
          {writeLabel === undefined || onWrite === undefined ? null : (
            <Tooltip label={writeLabel} withArrow>
              <ActionIcon variant="subtle" aria-label={writeLabel} disabled={locked} onClick={onWrite}>
                <PencilLine size={16} aria-hidden />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </div>
      <div className={classes.editor} data-busy={writing || undefined}>
        {lines === null ? (
          <Text className={classes.hint}>
            Press
            <WandSparkles size={14} className={classes.hintIcon} aria-label="the wand" />
            {hint.wand}
            {hint.pencil === undefined ? null : (
              <>
                <PencilLine size={14} className={classes.hintIcon} aria-label="the pencil" />
                {hint.pencil}
              </>
            )}
          </Text>
        ) : (
          <div className={classes.lines}>
            {head}
            {lines.map((line, index) => {
              const words = countUnits(line.text);
              const most = maxWords(index);
              return (
                <Fragment key={line.id}>
                  {lineHead?.(index)}
                  <div className={classes.line}>
                    <span className={classes.number}>{index + 1}</span>
                    {line.speaker === null ? null : (
                      <span className={classes.speaker} data-primary={line.speaker === primary || undefined}>
                        {line.speaker}
                      </span>
                    )}
                    <span className={classes.beat} title={line.beat}>
                      {line.beat}
                    </span>
                    <Textarea
                      className={classes.lineText}
                      classNames={{ input: classes.lineInput }}
                      variant="unstyled"
                      autosize
                      minRows={1}
                      maxLength={MAX_LINE_LENGTH}
                      aria-label={[`Line ${index + 1}`, line.speaker, line.beat].filter(Boolean).join(", ")}
                      value={line.text}
                      disabled={locked}
                      onChange={(event) => onLine(index, event.currentTarget.value)}
                    />
                    <span className={classes.count} data-over={words > most || undefined}>
                      {words}/{most}
                    </span>
                  </div>
                  {lineFoot?.(index)}
                </Fragment>
              );
            })}
            {foot}
          </div>
        )}
        {writing ? (
          <div className={classes.editorLoader}>
            <Loader size="sm" aria-label="Writing the script" />
          </div>
        ) : null}
      </div>
      {issue === null ? null : <Text className={classes.issue}>{issue}</Text>}
      {warning === null ? null : <Text className={classes.warning}>{warning}</Text>}
    </section>
  );
}
